import type {
    HybrisProductException,
    HybrisRecipeBase,
    HybrisResolveRecipesRequest,
} from '../../common/api/recipe/requests/directRecipeEcommerceApiRequests';
import type {
    HybrisJoinedRecipe,
    HybrisRecipe,
    HybrisRecipeList,
    HybrisRecipePrice,
    HybrisRecipeProductData,
    HybrisSubstituteResponse,
} from '../../common/api/recipe/responses/directRecipeEcommerceApiResponses';
import type { HybrisPrice } from '../../common/models/hybris/hybrisPrice';
import type { PriceData } from '../../common/models/priceData/priceData';
import type {
    ApiRecipePrice,
    BelongsToRecipeItem,
    RecipeProductItem,
    RecipeProductSubstitute,
    RecipeSubstitutes,
    ResolvedRecipe,
    ResolvedRecipesList,
    ResolveRecipesRequest,
} from '../models';

const mapToApiBelongsToRecipeItem = (from: HybrisJoinedRecipe): BelongsToRecipeItem => ({
    recipeId: from.code,
    rating: from.rating,
    name: from.title,
});

const mapToPriceData = (from?: HybrisPrice): PriceData => {
    return {
        inclTaxPrice: from?.value,
        exclTaxPrice: from?.valueWithoutTax,
    };
};

const mapToApiRecipeProductItem = (from: HybrisRecipeProductData): RecipeProductItem => {
    const imageUrl = from.images?.find((img) => img.url && img.url.startsWith('https://'))?.url;

    return {
        id: from.code,
        name: from.name, // Note: C# uses ToEcommerceName() extension method
        substituteIdentifier: from.substituteIdentifier,
        substituteProductsAmount: from.substituteProductsAmount,
        manufacturer: from.manufacturer || '',
        packageSizeInformation: from.packageSizeInformation,
        purchasable: from.purchasable,
        quantity: from.quantity,
        belongsToRecipes: from.recipes?.map(mapToApiBelongsToRecipeItem) || [],
        selectedVarianceCode: from.selectedVariance?.code || undefined,
        priceData: mapToPriceData(from.pickPrice || from.price),
        rowTotalPriceData: mapToPriceData(from.linePrice),
        image: imageUrl,
    };
};

export const mapToSubstituteResponse = (from: HybrisSubstituteResponse): RecipeSubstitutes => ({
    recipeProductItems: from.recipeProductsData.map(mapToApiRecipeProductItem),
});

const mapToApiRecipePrice = (from: HybrisRecipePrice): ApiRecipePrice => ({
    consumptionPortion: from.consumptionPortion,
    consumptionTotal: from.consumptionTotal,
    portion: mapToPriceData(from.portion),
    total: mapToPriceData(from.total),
});

const mapToApiHybrisRecipe = (from: HybrisRecipe): ResolvedRecipe => {
    const imageUrl = from.recipeImages?.find(
        (img) => img.url && img.url.startsWith('https://'),
    )?.url;

    return {
        cookingTime: from.cookingTime,
        id: from.id,
        code: from.code,
        portions: from.portions,
        rating: from.rating || 0,
        title: from.title,
        recipePrice: mapToApiRecipePrice(from.recipePrice),
        imageUrl: imageUrl || '',
    };
};

export const mapRecipeListToApiRecipeList = (source: HybrisRecipeList): ResolvedRecipesList => ({
    singleProducts: source.singleProducts?.map(mapToApiRecipeProductItem) || [],
    sharedProducts: source.sharedProducts?.map(mapToApiRecipeProductItem) || [],
    goodToHaveProducts: source.goodToHaveProducts?.map(mapToApiRecipeProductItem) || [],
    recipesProductsTotalAmount: source.recipesProductsTotalAmount,
    recipesTotalPrice: {
        inclTaxPrice: source.recipesTotalPrice?.value,
        exclTaxPrice: source.recipesTotalPrice?.valueWithoutTax,
    },
    recipes: source.recipes.map(mapToApiHybrisRecipe),
    calculation: source.calculation,
    prioritization: source.prioritization,
});

const mapToHybrisRecipeBase = (from: { recipeId: string; portions: number }): HybrisRecipeBase => ({
    code: from.recipeId,
    portions: from.portions,
});

const mapToHybrisProductException = (from: RecipeProductSubstitute): HybrisProductException => ({
    productId: from.productId,
    varianceCode: from.variantId,
    qty: from.qty === null ? undefined : from.qty,
    substituteId: from.substituteId,
});

export const mapToHybrisRecipesRequest = (
    from: ResolveRecipesRequest,
): HybrisResolveRecipesRequest => ({
    calculation: from.calculation,
    prioritization: from.prioritization || undefined,
    recipes: from.recipes.map(mapToHybrisRecipeBase),
    productExceptions: from.products?.map(mapToHybrisProductException) || [],
});
